(() => {
  const productStickyControllers = new Map();
  const CART_DRAWER_SECTION = 'stellr-cart-drawer';
  const CART_PAGE_SECTION = 'stellr-cart-main';
  const CART_SECTIONS = `${CART_DRAWER_SECTION},${CART_PAGE_SECTION}`;

  const money = (cents, currency, locale, includeCurrencyCode = false) => {
    const activeCurrency = currency || 'USD';
    const formatted = new Intl.NumberFormat(locale || 'en', {
      style: 'currency',
      currency: activeCurrency,
      currencyDisplay: 'narrowSymbol',
    }).format((Number(cents) || 0) / 100);

    return includeCurrencyCode ? `${formatted} ${activeCurrency}` : formatted;
  };

  const showMedia = (root, mediaId) => {
    if (!mediaId) return;
    window.StellrProductGallery?.setActiveMedia(root, mediaId);
    root.querySelectorAll('[data-media-id]').forEach((media) => {
      if (media.closest('[data-product-hero-gallery]')) return;
      const active = media.dataset.mediaId === String(mediaId);
      media.hidden = !active;
      media.classList.toggle('is-active', active);
    });
    root.querySelectorAll('[data-media-target]').forEach((button) => {
      if (button.dataset.mediaTarget === String(mediaId)) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
  };

  const sectionIdMatches = (candidate, expected) => {
    if (!candidate || !expected) return false;
    return candidate === expected || candidate.endsWith(`__${expected}`) || expected.endsWith(`__${candidate}`);
  };

  const getProductScopes = (root) => {
    const scopes = [root];
    const sectionId = root?.dataset?.sectionId;
    if (!sectionId) return scopes;

    document.querySelectorAll('[data-product-sync-root]').forEach((scope) => {
      if (sectionIdMatches(sectionId, scope.dataset.productSyncRoot) && scope !== root) scopes.push(scope);
    });

    return scopes;
  };

  const updateVariant = (root, option) => {
    const available = option.dataset.available !== 'false';
    const variantId = option.value;
    const currency = root.dataset.currency;
    const locale = root.dataset.locale;
    const variantLabel = option.dataset.variantLabel || option.dataset.variantTitle || '';
    const variantTheme =
      option.dataset.variantTheme ||
      (/blossom|pink/i.test(variantLabel) ? 'blossom' : 'cobalt');
    const variantAccent = variantTheme === 'blossom' ? '#ff6fa8' : '#5170ff';

    root.dataset.variantTheme = variantTheme;
    root.style.setProperty('--stellr-product-accent', variantAccent);
    root.style.setProperty('--stellr-accent', variantAccent);

    getProductScopes(root).forEach((scope) => {
      scope.style?.setProperty('--stellr-product-accent', variantAccent);
      scope.style?.setProperty('--stellr-accent', variantAccent);
      scope.querySelectorAll('input[data-variant-select]').forEach((input) => {
        if (input !== option) input.value = variantId;
      });
      scope.querySelectorAll('[data-variant-option]').forEach((input) => {
        input.checked = input.value === variantId;
      });
      scope.querySelectorAll('[data-price-current]').forEach((currentPrice) => {
        const includeCurrencyCode = currentPrice.closest('[data-product-price]')?.dataset.priceIncludeCode === 'true';
        if (option.dataset.price) currentPrice.textContent = money(option.dataset.price, currency, locale, includeCurrencyCode);
      });
      scope.querySelectorAll('[data-price-compare]').forEach((comparePrice) => {
        const compare = Number(option.dataset.comparePrice || 0);
        const current = Number(option.dataset.price || 0);
        const includeCurrencyCode = comparePrice.closest('[data-product-price]')?.dataset.priceIncludeCode === 'true';
        comparePrice.hidden = compare <= current;
        comparePrice.textContent = compare > current ? money(compare, currency, locale, includeCurrencyCode) : '';
      });
      scope.querySelectorAll('[data-add-to-cart]').forEach((submit) => {
        submit.disabled = !available;
      });
      scope.querySelectorAll('[data-add-to-cart-label]').forEach((label) => {
        label.textContent = available ? 'Add to cart' : 'Sold out';
      });
      scope.querySelectorAll('[data-selected-option-label]').forEach((selectedOptionLabel) => {
        selectedOptionLabel.textContent = variantLabel;
      });
      scope.querySelectorAll('[data-detail-selected-label]').forEach((detailSelectedLabel) => {
        detailSelectedLabel.textContent = variantLabel;
      });
      scope.querySelectorAll('[data-detail-selected-dot]').forEach((detailSelectedDot) => {
        if (option.dataset.variantColor) detailSelectedDot.style.setProperty('--swatch-color', option.dataset.variantColor);
      });
    });

    root.querySelectorAll('[data-variant-included]').forEach((panel) => {
      panel.hidden = panel.dataset.variantIncluded !== String(variantId);
    });
    root.querySelectorAll('[data-variant-detail]').forEach((panel) => {
      panel.hidden = panel.dataset.variantDetail !== String(variantId);
    });
    root.querySelectorAll('[data-detail-variant-target]').forEach((button) => {
      const active = button.dataset.detailVariantTarget === String(variantId);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    showMedia(root, option.dataset.mediaId);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  };

  const extractCartCountFromHtml = (html) => {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const count = parsed.querySelector('[data-cart-count]')?.textContent?.trim();
    if (count === undefined || count === null || count === '') return null;
    const numericCount = Number(count);
    return Number.isFinite(numericCount) ? numericCount : null;
  };

  const updateCartIndicators = (count) => {
    const numericCount = Number(count);
    if (!Number.isFinite(numericCount)) return;

    document.querySelectorAll('[data-cart-count]').forEach((indicator) => {
      indicator.textContent = String(numericCount);
      if (indicator.hasAttribute('data-cart-count-hide-empty')) indicator.hidden = numericCount <= 0;
    });
  };

  const setCartStatus = (drawer, message) => {
    const status = drawer?.querySelector('[data-cart-status]');
    if (!status) return;
    status.textContent = message || '';
    status.hidden = !message;
  };

  const setCartLineBusy = (trigger, busy) => {
    const line = trigger?.closest?.('[data-cart-item]');
    if (!line) {
      if (trigger) trigger.toggleAttribute('aria-busy', busy);
      return;
    }

    line.querySelectorAll('button, input').forEach((control) => {
      control.toggleAttribute('aria-busy', busy);
      if (control.matches('button')) control.disabled = busy;
      if (control.matches('input')) control.readOnly = busy;
    });
  };

  const playCartCelebration = (drawer) => {
    const template = document.querySelector('[data-cart-celebration-template]');
    const anchor = drawer?.querySelector('.stellr-cart-drawer__header');
    if (!(template instanceof HTMLTemplateElement) || !anchor) return;

    document.querySelectorAll('.stellr-cart-celebration').forEach((celebration) => celebration.remove());

    const celebration = template.content.firstElementChild?.cloneNode(true);
    if (!(celebration instanceof HTMLElement)) return;

    const anchorRect = anchor.getBoundingClientRect();
    celebration.style.left = `${Math.max(12, anchorRect.right - 126)}px`;
    celebration.style.top = `${Math.max(12, anchorRect.top + 2)}px`;
    drawer.appendChild(celebration);

    const stars = Array.from(celebration.querySelectorAll('.stellr-cart-celebration__star'));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion || !window.gsap) {
      celebration.classList.add('is-reduced-motion');
      window.setTimeout(() => celebration.remove(), 700);
      return;
    }

    const directions = [
      { x: 0, y: -18, rotation: 22, scale: 1 },
      { x: -30, y: 18, rotation: -28, scale: 0.78 },
      { x: 30, y: 12, rotation: 32, scale: 0.82 },
    ];

    window.gsap.set(celebration, { autoAlpha: 1 });
    const timeline = window.gsap.timeline({ onComplete: () => celebration.remove() });
    timeline.fromTo(
      stars,
      { autoAlpha: 0, scale: 0.2, rotation: 0, x: 0, y: 0, force3D: true },
      {
        autoAlpha: 1,
        x: (index) => directions[index]?.x || 0,
        y: (index) => directions[index]?.y || 0,
        rotation: (index) => directions[index]?.rotation || 0,
        scale: (index) => directions[index]?.scale || 1,
        duration: 0.46,
        ease: 'back.out(2)',
        stagger: 0.045,
        force3D: true,
      },
    );
    timeline.to(stars, {
      autoAlpha: 0,
      y: '+=12',
      scale: 0.72,
      duration: 0.18,
      ease: 'power1.in',
      stagger: 0.025,
    }, '+=0.28');
  };

  const replaceCartDrawer = (html) => {
    if (!html) return null;
    const current = document.querySelector('#shopify-section-stellr-cart-drawer');
    if (!current) return;
    const nextCount = extractCartCountFromHtml(html);
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const currentDialog = current.querySelector('#StellrCartDrawer');
    const replacementDialog = parsed.querySelector('#StellrCartDrawer');
    if (currentDialog && replacementDialog) {
      const preserveAttributes = new Set(['open', 'data-cart-drawer-bound']);
      Array.from(currentDialog.attributes).forEach((attribute) => {
        if (!preserveAttributes.has(attribute.name)) currentDialog.removeAttribute(attribute.name);
      });
      Array.from(replacementDialog.attributes).forEach((attribute) => {
        if (attribute.name !== 'open') currentDialog.setAttribute(attribute.name, attribute.value);
      });
      currentDialog.innerHTML = replacementDialog.innerHTML;
      return nextCount;
    }

    const replacement = parsed.querySelector('#shopify-section-stellr-cart-drawer');
    if (replacement) current.replaceWith(replacement);
    return nextCount;
  };

  const replaceCartPage = (html) => {
    if (!html) return null;
    const current = document.querySelector('[data-cart-page]');
    if (!current) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const replacement = parsed.querySelector('[data-cart-page]');
    if (!replacement) return null;
    current.replaceWith(replacement);
    bindCartPage(replacement);
    init(replacement);
    return replacement;
  };

  const refreshCartDrawerFromResponse = (result, opener, shouldOpenDrawer = true) => {
    replaceCartPage(result.sections?.[CART_PAGE_SECTION]);
    const renderedCount = replaceCartDrawer(result.sections?.[CART_DRAWER_SECTION]);
    updateCartIndicators(result.item_count ?? renderedCount);
    const drawer = document.querySelector('#StellrCartDrawer');
    if (shouldOpenDrawer && drawer instanceof HTMLDialogElement && !drawer.open) window.StellrTheme?.openDialog(drawer, opener);
    init(drawer || document);
    return drawer;
  };

  const submitProduct = async (form) => {
    const root = form.closest('[data-product-root]');
    const status = form.querySelector('[data-product-status]') || root?.querySelector('[data-product-status]');
    const submit = form.querySelector('[data-add-to-cart], button[type="submit"]');
    const data = new FormData(form);
    data.append('sections', CART_SECTIONS);
    data.append('sections_url', window.location.pathname);
    submit?.setAttribute('aria-busy', 'true');
    if (submit) submit.disabled = true;
    if (status) status.textContent = '';

    try {
      const response = await fetch(form.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: data,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this item.');
      const drawer = refreshCartDrawerFromResponse(result, submit);
      playCartCelebration(drawer);
      if (status) status.textContent = 'Added to cart.';
    } catch (error) {
      if (status) status.textContent = error.message;
    } finally {
      submit?.removeAttribute('aria-busy');
      const selected =
        form.querySelector('[data-variant-option]:checked') ||
        form.querySelector('[data-variant-select] option:checked') ||
        form.querySelector('[data-variant-select]');
      if (submit) submit.disabled = selected?.dataset.available === 'false';
    }
  };

  const bindProductRoot = (root) => {
    if (root.dataset.commerceBound === 'true') return;
    root.dataset.commerceBound = 'true';

    root.addEventListener('change', (event) => {
      if (!event.target.matches('[data-variant-select], [data-variant-option]')) return;
      const option = event.target.matches('[data-variant-option]')
        ? event.target
        : event.target.selectedOptions?.[0] || event.target;
      if (option) updateVariant(root, option);
    });

    root.addEventListener('click', (event) => {
      const detailVariantButton = event.target.closest('[data-detail-variant-target]');
      if (detailVariantButton) {
        event.preventDefault();
        const variantInput = root.querySelector(`[data-variant-option][value="${detailVariantButton.dataset.detailVariantTarget}"]`);
        if (variantInput && !variantInput.disabled) {
          variantInput.checked = true;
          updateVariant(root, variantInput);
        }
        return;
      }

      const mediaButton = event.target.closest('[data-media-target]');
      if (mediaButton) showMedia(root, mediaButton.dataset.mediaTarget);

      const quantity = event.target.closest('[data-quantity]');
      if (!quantity) return;
      const input = quantity.querySelector('input');
      if (!input) return;
      if (event.target.closest('[data-quantity-minus]')) input.value = Math.max(1, Number(input.value || 1) - 1);
      if (event.target.closest('[data-quantity-plus]')) input.value = Math.max(1, Number(input.value || 1) + 1);
    });

    bindMobileProductBars(root);

  };

  const bindProductForm = (form) => {
    if (form.dataset.productFormBound === 'true') return;
    form.dataset.productFormBound = 'true';
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submitProduct(event.currentTarget);
    });
  };

  const updateCartLine = async (drawer, key, quantity, trigger) => {
    if (!key) return;
    const nextQuantity = Math.max(0, Number(quantity || 0));
    setCartStatus(drawer, '');
    setCartLineBusy(trigger, true);
    try {
      const response = await fetch(drawer.dataset.cartChangeUrl || '/cart/change.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: nextQuantity,
          sections: CART_SECTIONS,
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to update cart.');
      refreshCartDrawerFromResponse(result, null, false);
    } catch (error) {
      setCartStatus(drawer, error.message || 'Unable to update cart.');
      setCartLineBusy(trigger, false);
    } finally {
      setCartLineBusy(trigger, false);
    }
  };

  const addCombo = async (button) => {
    const fallbackVariantIds = (button.dataset.variantIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    const comboVariantIds = [button.dataset.comboCobaltId, button.dataset.comboBlossomId].filter(Boolean);
    const candidateVariantIds = comboVariantIds.length ? comboVariantIds : fallbackVariantIds;
    const offer = button.closest('[data-combo-offer], .stellr-cart-combo');
    const status = offer?.querySelector('[data-combo-status]');
    if (!candidateVariantIds.length) return;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (status) status.textContent = '';
    try {
      const cartResponse = await fetch(button.dataset.cartUrl || '/cart.js', {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();
      if (!cartResponse.ok) throw new Error('Unable to check your cart.');

      const cartVariantIds = new Set((cart.items || []).map((item) => String(item.variant_id)));
      const missingVariantIds = candidateVariantIds.filter((id) => !cartVariantIds.has(String(id)));

      if (!missingVariantIds.length) {
        button.textContent = 'combo complete';
        if (status) status.textContent = 'both colours are already in your cart.';
        const drawer = document.querySelector('#StellrCartDrawer');
        if (drawer instanceof HTMLDialogElement && !drawer.open) window.StellrTheme?.openDialog(drawer, button);
        return;
      }

      const response = await fetch(button.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: missingVariantIds.map((id) => ({ id: Number(id), quantity: 1 })),
          sections: CART_SECTIONS,
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to add combo.');
      const refreshedDrawer = refreshCartDrawerFromResponse(result, button);
      playCartCelebration(refreshedDrawer);
      if (status) status.textContent = missingVariantIds.length === 1 ? 'the other colour is now in your cart.' : 'both colours are now in your cart.';
    } catch (error) {
      button.disabled = false;
      button.dataset.error = error.message;
      const drawer = button.closest('[data-cart-drawer]');
      if (drawer) setCartStatus(drawer, error.message || 'Unable to add combo.');
      if (status) status.textContent = error.message || 'Unable to add combo.';
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  };

  const bindComboOffer = (button) => {
    if (button.dataset.comboBound === 'true') return;
    button.dataset.comboBound = 'true';
    button.addEventListener('click', (event) => {
      event.preventDefault();
      addCombo(event.currentTarget);
    });
  };

  const bindCartDrawer = (drawer) => {
    if (!drawer || drawer.dataset.cartDrawerBound === 'true') return;
    drawer.dataset.cartDrawerBound = 'true';

    drawer.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        updateCartLine(drawer, removeButton.dataset.cartKey, 0, removeButton);
        return;
      }

      const quantity = event.target.closest('[data-cart-quantity]');
      if (!quantity) return;
      const input = quantity.querySelector('[data-cart-quantity-input]');
      if (!input) return;

      if (event.target.closest('[data-cart-quantity-minus]')) {
        event.preventDefault();
        const nextQuantity = Math.max(0, Number(input.value || 0) - 1);
        input.value = String(nextQuantity);
        updateCartLine(drawer, input.dataset.cartKey, nextQuantity, event.target.closest('button'));
      }

      if (event.target.closest('[data-cart-quantity-plus]')) {
        event.preventDefault();
        const nextQuantity = Number(input.value || 0) + 1;
        input.value = String(nextQuantity);
        updateCartLine(drawer, input.dataset.cartKey, nextQuantity, event.target.closest('button'));
      }
    });

    drawer.addEventListener('change', (event) => {
      if (!event.target.matches('[data-cart-quantity-input]')) return;
      const nextQuantity = Math.max(0, Number(event.target.value || 0));
      event.target.value = String(nextQuantity);
      updateCartLine(drawer, event.target.dataset.cartKey, nextQuantity, event.target);
    });
  };

  const setCartPageStatus = (page, message = '') => {
    const status = page?.querySelector('[data-cart-page-status]');
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
  };

  const setCartPageLineBusy = (trigger, busy) => {
    if (!trigger) return;
    trigger.toggleAttribute('aria-busy', busy);
    if ('disabled' in trigger) trigger.disabled = busy;
  };

  const updateCartPageLine = async (page, key, quantity, trigger) => {
    if (!key) return;
    const nextQuantity = Math.max(0, Number(quantity || 0));
    setCartPageStatus(page);
    setCartPageLineBusy(trigger, true);

    try {
      const response = await fetch(page.dataset.cartChangeUrl || '/cart/change.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: nextQuantity,
          sections: CART_SECTIONS,
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to update cart.');
      refreshCartDrawerFromResponse(result, null, false);
    } catch (error) {
      setCartPageStatus(page, error.message || 'Unable to update cart.');
      setCartPageLineBusy(trigger, false);
    }
  };

  const bindCartPage = (page) => {
    if (!page || page.dataset.cartPageBound === 'true') return;
    page.dataset.cartPageBound = 'true';

    page.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-cart-page-remove]');
      if (removeButton) {
        event.preventDefault();
        updateCartPageLine(page, removeButton.dataset.cartKey, 0, removeButton);
        return;
      }

      const quantity = event.target.closest('[data-cart-page-quantity]');
      if (!quantity) return;
      const input = quantity.querySelector('[data-cart-page-quantity-input]');
      if (!input) return;

      if (event.target.closest('[data-cart-page-quantity-minus]')) {
        event.preventDefault();
        const nextQuantity = Math.max(0, Number(input.value || 0) - 1);
        input.value = String(nextQuantity);
        updateCartPageLine(page, input.dataset.cartKey, nextQuantity, event.target.closest('button'));
      }

      if (event.target.closest('[data-cart-page-quantity-plus]')) {
        event.preventDefault();
        const nextQuantity = Number(input.value || 0) + 1;
        input.value = String(nextQuantity);
        updateCartPageLine(page, input.dataset.cartKey, nextQuantity, event.target.closest('button'));
      }
    });

    page.addEventListener('change', (event) => {
      if (!event.target.matches('[data-cart-page-quantity-input]')) return;
      const nextQuantity = Math.max(0, Number(event.target.value || 0));
      event.target.value = String(nextQuantity);
      updateCartPageLine(page, event.target.dataset.cartKey, nextQuantity, event.target);
    });
  };

  const bindPredictiveSearch = (form) => {
    if (form.dataset.predictiveBound === 'true') return;
    form.dataset.predictiveBound = 'true';
    const input = form.querySelector('input[type="search"]');
    const results = document.querySelector('[data-predictive-search-results]');
    let timer;

    input?.addEventListener('input', () => {
      window.clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) {
        if (results) results.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
        return;
      }

      timer = window.setTimeout(async () => {
        const route = form.dataset.predictiveUrl;
        if (!route) return;
        const url = `${route}?q=${encodeURIComponent(query)}&section_id=stellr-predictive-search`;
        const response = await fetch(url);
        if (!response.ok) return;
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = documentFragment.querySelector('[data-predictive-search-results]');
        if (results && incoming) results.innerHTML = incoming.innerHTML;
        input.setAttribute('aria-expanded', incoming?.textContent.trim() ? 'true' : 'false');
      }, 180);
    });
  };

  const findProductRootForSyncedElement = (element) => {
    const syncRoot = element.closest('[data-product-sync-root]');
    if (!syncRoot) return null;
    const sectionId = syncRoot.dataset.productSyncRoot;
    return Array.from(document.querySelectorAll('[data-product-root]')).find((root) => sectionIdMatches(root.dataset.sectionId, sectionId)) || null;
  };

  const bindMobileProductBar = (bar) => {
    if (bar.dataset.mobileProductBarBound !== 'true') {
      bar.dataset.mobileProductBarBound = 'true';
      bar.addEventListener('change', (event) => {
        if (!event.target.matches('[data-variant-select], [data-variant-option]')) return;
        const productRoot = findProductRootForSyncedElement(event.target);
        if (!productRoot) return;
        const option = event.target.matches('[data-variant-option]')
          ? event.target
          : event.target.selectedOptions?.[0] || event.target;
        if (option) updateVariant(productRoot, option);
      });
    }

    bar.querySelectorAll('[data-product-form]').forEach(bindProductForm);
  };

  const bindMobileProductBars = (root) => {
    const sectionId = root.dataset.sectionId;
    if (!sectionId) return;

    document.querySelectorAll('[data-mobile-product-bar]').forEach((bar) => {
      if (!sectionIdMatches(sectionId, bar.dataset.productSyncRoot)) return;
      bindMobileProductBar(bar);
    });
  };

  const setProductFloatingBarVisible = (floatingBar, visible) => {
    if (!floatingBar || floatingBar.classList.contains('is-visible') === visible) return;
    floatingBar.classList.toggle('is-visible', visible);
    floatingBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
  };

  const destroyProductStickyController = (controllerRoot) => {
    const controller = productStickyControllers.get(controllerRoot);
    controller?.inlineObserver?.disconnect();
    controller?.footerObserver?.disconnect();
    productStickyControllers.delete(controllerRoot);
  };

  const updateProductStickyController = (controller) => {
    const { controllerRoot, floatingBar } = controller;
    if (!controllerRoot.isConnected || !floatingBar.isConnected) return;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    setProductFloatingBarVisible(floatingBar, isMobile && controller.inlinePassed && !controller.footerReached);
  };

  const setupProductStickyObservers = (controller) => {
    controller.inlineObserver?.disconnect();
    controller.footerObserver?.disconnect();

    if (!('IntersectionObserver' in window)) {
      controller.inlinePassed = false;
      controller.footerReached = true;
      updateProductStickyController(controller);
      return;
    }

    if (controller.inlineCta) {
      controller.inlineObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        controller.inlinePassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        updateProductStickyController(controller);
      }, { threshold: 0 });
      controller.inlineObserver.observe(controller.inlineCta);
    } else {
      controller.inlinePassed = false;
    }

    controller.footerObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      controller.footerReached = entry.isIntersecting || entry.boundingClientRect.top <= 0;
      updateProductStickyController(controller);
    }, {
      rootMargin: '0px 0px -138px 0px',
      threshold: 0,
    });
    controller.footerObserver.observe(controller.footerBar);
  };

  const setupProductStickyController = (controllerRoot) => {
    if (productStickyControllers.has(controllerRoot)) return;

    const footerBar = controllerRoot.querySelector('[data-mobile-product-footer-bar]');
    const floatingBar = controllerRoot.querySelector('[data-mobile-product-floating]');
    const syncRoot = floatingBar?.dataset.productSyncRoot;
    if (!footerBar || !floatingBar || !syncRoot) return;

    const productRoot = Array.from(document.querySelectorAll('[data-product-root]')).find((root) =>
      sectionIdMatches(root.dataset.sectionId, syncRoot),
    );
    const inlineCta = productRoot?.querySelector('[data-product-inline-atc]');
    const controller = {
      controllerRoot,
      footerBar,
      floatingBar,
      inlineCta,
      inlinePassed: false,
      footerReached: false,
      inlineObserver: null,
      footerObserver: null,
    };
    productStickyControllers.set(controllerRoot, controller);
    setupProductStickyObservers(controller);
    updateProductStickyController(controller);
  };

  const findProductStickyControllers = (root) => {
    const controllers = Array.from(root.querySelectorAll?.('[data-mobile-product-sticky]') || []);
    if (root.matches?.('[data-mobile-product-sticky]')) controllers.unshift(root);
    return controllers;
  };

  const syncProductStickyControllersAfterScroll = () => {
    if (!productStickyControllers.size) return;

    const viewportBottom = document.documentElement.clientHeight - 138;
    productStickyControllers.forEach((controller) => {
      if (!controller.inlineCta?.isConnected || !controller.footerBar?.isConnected) return;

      // IntersectionObserver owns normal transitions. This only reconciles a completed fast scroll.
      controller.inlinePassed = controller.inlineCta.getBoundingClientRect().bottom <= 0;
      controller.footerReached = controller.footerBar.getBoundingClientRect().top <= viewportBottom;
      updateProductStickyController(controller);
    });
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-product-root]').forEach(bindProductRoot);
    root.querySelectorAll('[data-mobile-product-bar]').forEach(bindMobileProductBar);
    root.querySelectorAll('[data-product-form]').forEach(bindProductForm);
    root.querySelectorAll('[data-predictive-search-form]').forEach(bindPredictiveSearch);
    root.querySelectorAll('[data-cart-drawer]').forEach(bindCartDrawer);
    root.querySelectorAll('[data-cart-page]').forEach(bindCartPage);
    root.querySelectorAll('[data-combo-complete]').forEach(bindComboOffer);
    findProductStickyControllers(root).forEach(setupProductStickyController);
  };

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    findProductStickyControllers(event.target).forEach(destroyProductStickyController);
  });
  document.addEventListener('scrollend', syncProductStickyControllersAfterScroll, { passive: true });
})();
